import React, { useContext, useEffect, useMemo, useRef } from "react";
import { ClavaContext } from "../../../config/contexts";
import { AdPositionEnum } from "../../../client/api";
import { ADSENSE_CLIENT_ID, ADSENSE_CONFIG, AdSensePositionEnum } from "../../../config/adsenseConfig";

interface WindowWithAdSense extends Window {
  adsbygoogle?: Array<Record<string, never>>;
}

type AdSenseAdProps = {
  position: AdPositionEnum | AdSensePositionEnum;
  className?: string;
};

let scriptLoaded = false;
let scriptLoading = false;

const MIN_AD_WIDTH = 250;

const hasValidDimensions = (element: HTMLElement | null, isFluid: boolean): boolean => {
  if (!element) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  const { width } = rect;
  const computedStyle = window.getComputedStyle(element);

  const hasValidWidth = width >= MIN_AD_WIDTH;
  const isVisible = computedStyle.display !== "none" &&
    computedStyle.visibility !== "hidden" &&
    computedStyle.opacity !== "0";

  if (isFluid) {
    return hasValidWidth && isVisible;
  }

  return hasValidWidth && rect.height > 0 && isVisible;
};

const AdSenseAd: React.FC<AdSenseAdProps> = ({ position, className }) => {
  const config = useMemo(() => ADSENSE_CONFIG[position], [position]);
  const { pro } = useContext(ClavaContext);
  const insRef = useRef<HTMLModElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (scriptLoaded) {
      return undefined;
    }

    if (scriptLoading) {
      return undefined;
    }

    const existingScript = document.querySelector(
      "script[src*=\"pagead2.googlesyndication.com/pagead/js/adsbygoogle.js\"]"
    );

    if (existingScript) {
      scriptLoaded = true;
      return undefined;
    }

    scriptLoading = true;
    const script = document.createElement("script");
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.async = true;
    script.crossOrigin = "anonymous";

    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
    };

    script.onerror = () => {
      scriptLoading = false;
    };

    document.head.appendChild(script);

    return undefined;
  }, [position, pro]);

  useEffect(() => {
    if (!config || pro || initializedRef.current || !insRef.current) {
      return undefined;
    }

    const isFluid = config.adFormat === "fluid";
    let resizeObserver: ResizeObserver | null = null;
    let checkInterval: ReturnType<typeof setInterval> | null = null;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const tryInitialize = () => {
      if (!insRef.current || initializedRef.current) {
        return false;
      }

      if (isFluid) {
        const isValid = hasValidDimensions(insRef.current, true);
        if (!isValid) {
          return false;
        }
      }

      try {
        const windowWithAdSense = window as WindowWithAdSense;
        if (typeof window !== "undefined" && windowWithAdSense.adsbygoogle) {
          (windowWithAdSense.adsbygoogle = windowWithAdSense.adsbygoogle || []).push({});
          initializedRef.current = true;
          return true;
        }
      } catch {
        // AdSense initialization errors are handled silently
      }
      return false;
    };

    const setupResizeObserver = () => {
      if (!insRef.current || initializedRef.current) {
        return;
      }

      resizeObserver = new ResizeObserver(() => {
        if (tryInitialize() && resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
          if (timeout) {
            clearTimeout(timeout);
            timeout = null;
          }
        }
      });

      resizeObserver.observe(insRef.current);

      timeout = setTimeout(() => {
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
      }, 30000);
    };

    let fallbackTimeout: ReturnType<typeof setTimeout> | null = null;
    const windowWithAdSense = window as WindowWithAdSense;

    const attemptInit = () => {
      if (windowWithAdSense.adsbygoogle) {
        if (!tryInitialize() && isFluid) {
          setupResizeObserver();
          fallbackTimeout = setTimeout(() => {
            if (!initializedRef.current && insRef.current) {
              const rect = insRef.current.getBoundingClientRect();
              if (rect.width > 0) {
                tryInitialize();
              }
            }
          }, 2000);
        }
      } else {
        checkInterval = setInterval(() => {
          if (windowWithAdSense.adsbygoogle) {
            if (checkInterval) {
              clearInterval(checkInterval);
              checkInterval = null;
            }
            if (!tryInitialize() && isFluid) {
              setupResizeObserver();
            }
          }
        }, 100);

        timeout = setTimeout(() => {
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
        }, 10000);
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        attemptInit();
      });
    });

    return () => {
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [config, pro, position]);

  if (!config) {
    return null;
  }

  if (pro) {
    return null;
  }

  const needsMinWidth = config.adFormat === "fluid";
  const containerStyle = needsMinWidth ? { minWidth: "250px" } : undefined;
  const insStyle = needsMinWidth
    ? { display: "block", minWidth: "250px" }
    : { display: "block" };

  return (
    <div className={className} style={containerStyle}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={insStyle}
        data-ad-format={config.adFormat}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={config.adSlot}
        {...(config.adFormat === "fluid" && config.adLayoutKey && { "data-ad-layout-key": config.adLayoutKey })}
        {...(config.fullWidthResponsive && { "data-full-width-responsive": "true" })}
      />
    </div>
  );
};

export default AdSenseAd;
